import {
  Button as MuiButton,
  DialogActions,
  Popover,
  ThemeProvider,
} from '@mui/material'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import { Button } from '@/Atomic/Molecules/Button'
import {
  DateRangeCalendar,
  DateRangeStringValue,
  useDateRangePopover,
} from '@/Atomic/Molecules/DateRangeCalendar'
import { calendarTheme } from '@/Atomic/Molecules/DateRangeCalendar/theme'

export type { DateRangeStringValue } from '@/Atomic/Molecules/DateRangeCalendar'

export interface DateRangePreset {
  label: string
  from: string
  to: string
}

export interface DateRangeDropdownProps {
  id?: string
  value: DateRangeStringValue
  onChange: (value: DateRangeStringValue) => void
  format?: string
  calendars?: 1 | 2
  maxDays?: number
  disabled?: boolean
  className?: string
  placeholder?: string
  size?: 'sm' | 'md' | 'lg'
  presets?: DateRangePreset[]
  // Extra class for the trigger button. Opt-in so the entity-list-specific
  // `.filter-button` background/border styling doesn't leak into other
  // consumers (e.g. Time Tracking).
  triggerClassName?: string
  // Chevron position. Defaults to 'right' to match the app-wide dropdown
  // convention (SimpleSelect, drawer/menu dropdowns). Time Tracking passes
  // 'left' to keep its original leading-chevron design.
  iconPosition?: 'left' | 'right'
}

export const DateRangeDropdown = ({
  id = 'date-range-dropdown',
  value,
  onChange,
  format = 'MMM D, YYYY',
  calendars,
  maxDays,
  disabled,
  className,
  placeholder,
  size = 'sm',
  presets,
  triggerClassName,
  iconPosition = 'right',
}: DateRangeDropdownProps) => {
  const { t } = useTranslation()
  const {
    anchorEl,
    pendingValue,
    setPendingValue,
    open,
    popoverId,
    handleOpen,
    handleAccept,
    handleCancel,
  } = useDateRangePopover({ id, value, onChange })

  const [from, to] = value
  const displayLabel =
    from && to
      ? `${dayjs(from).format(format)} – ${dayjs(to).format(format)}`
      : (placeholder ?? t('components.dateRangePicker.selectRange'))

  const handleTriggerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) handleOpen(e as React.MouseEvent<HTMLElement>)
  }

  const handlePresetClick = (preset: DateRangePreset) => {
    setPendingValue([dayjs(preset.from), dayjs(preset.to)])
  }

  const handleClear = () => {
    onChange([null, null])
    // Clear commits and closes, matching OK's commit-and-close behaviour.
    // handleCancel closes the popover; the next open reseeds pendingValue from
    // the now-empty committed value, so no separate reset is needed here.
    handleCancel()
  }

  const isPresetActive = (preset: DateRangePreset): boolean => {
    if (!pendingValue[0] || !pendingValue[1]) {
      return false
    }
    return (
      pendingValue[0].format('YYYY-MM-DD') === preset.from &&
      pendingValue[1].format('YYYY-MM-DD') === preset.to
    )
  }

  const hasPending = !!(pendingValue[0] && pendingValue[1])
  const hasCommitted = !!(from && to)
  const canClear = hasPending || hasCommitted

  return (
    <div className={classNames(className)}>
      <Button
        variant="outlined"
        title={displayLabel}
        tooltip={displayLabel}
        icon="chevron-down"
        reverse={iconPosition === 'right'}
        size={size}
        buttonClassName={triggerClassName}
        onClick={handleTriggerClick}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
      />
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        // Portal to <body> (MUI default) so the popover is a sibling of #root,
        // not a descendant — this avoids the aria-hidden-on-focused-ancestor
        // warning that `disablePortal` caused. Raise z-index above the mobile
        // filter sheet (z-index 2001) so it isn't hidden behind the sheet.
        sx={{ zIndex: 2100 }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '4px',
              borderRadius: '8px',
              boxShadow: '0 16px 32px 0 rgba(17, 17, 17, 0.13)',
            },
          },
        }}
      >
        <ThemeProvider theme={calendarTheme}>
          {presets && presets.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
                padding: '8px 12px',
                borderBottom: '1px solid rgba(0,0,0,0.12)',
              }}
            >
              {presets.map((preset) => (
                <MuiButton
                  key={preset.label}
                  size="small"
                  variant={isPresetActive(preset) ? 'contained' : 'outlined'}
                  onClick={() => handlePresetClick(preset)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    minWidth: 0,
                    padding: '2px 8px',
                  }}
                >
                  {preset.label}
                </MuiButton>
              ))}
            </div>
          )}
          <DateRangeCalendar
            value={pendingValue}
            onChange={setPendingValue}
            calendars={calendars}
            maxDays={maxDays}
          />
          <DialogActions
            sx={{
              borderTop: '1px solid rgba(0,0,0,0.12)',
              justifyContent: 'space-between',
            }}
          >
            <MuiButton onClick={handleClear} disabled={!canClear}>
              {t('common.clear')}
            </MuiButton>
            <div>
              <MuiButton onClick={handleCancel}>{t('common.cancel')}</MuiButton>
              <MuiButton onClick={handleAccept} disabled={!hasPending}>
                {t('common.ok')}
              </MuiButton>
            </div>
          </DialogActions>
        </ThemeProvider>
      </Popover>
    </div>
  )
}
